// src/pages/Inventory/Inventory.js
import React, { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { inventoryService, productService, partTypeService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Tab } from '@headlessui/react';
import {
    PlusIcon,
    MinusIcon,
    AdjustmentsHorizontalIcon,
    DocumentArrowDownIcon,
    TableCellsIcon,
    PlusCircleIcon,
} from '@heroicons/react/24/outline';
import StockInModal from './StockInModal';
import StockOutModal from './StockOutModal';
import AdjustInventoryModal from './AdjustInventoryModal';

const Inventory = () => {
    const { hasAnyRole } = useAuth();
    const [inventory, setInventory] = useState({
                                                   general_inventory: [],
                                                   sku_specific_inventory: []
                                               });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isStockInModalOpen, setIsStockInModalOpen] = useState(false);
    const [isStockOutModalOpen, setIsStockOutModalOpen] = useState(false);
    const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [partTypes, setPartTypes] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
                                                     key: 'current_stock',
                                                     direction: 'desc'
                                                 });

    // Fetch inventory data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch inventory data
                const inventoryResponse = await inventoryService.getInventory();
                setInventory(inventoryResponse.data);

                // Fetch products for dropdowns
                const productsResponse = await productService.getProducts();
                setProducts(productsResponse.data);

                // Fetch part types for dropdowns
                const partTypesResponse = await partTypeService.getPartTypes();
                setPartTypes(partTypesResponse.data);

                setError(null);
            } catch (err) {
                console.error('Error fetching inventory data:', err);
                setError('Failed to load inventory data. Please try again later.');
                toast.error('Failed to load inventory data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Generate a CSV export of current inventory
    const exportInventoryCSV = async () => {
        try {
            // Use the correct URL from the API routes
            const response = await inventoryService.exportInventoryReport({
                                                                              fileFormat: 'csv',
                                                                              report_type: 'current_stock'
                                                                          });

            // Create a download link for the CSV
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up by removing the link element

            toast.success('Inventory report exported successfully');
        } catch (error) {
            console.error('Error exporting inventory:', error);
            toast.error('Failed to export inventory report');
        }
    };

    // Handle stock in/out/adjust success
    const handleOperationSuccess = async () => {
        try {
            // Refresh inventory data
            const response = await inventoryService.getInventory();
            setInventory(response.data);
        } catch (error) {
            console.error('Error refreshing inventory data:', error);
            toast.error('Failed to refresh inventory data');
        }
    };

    // Sort inventory data
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted inventory data
    const getSortedData = (data) => {
        if (!data || data.length === 0) return [];

        const sortableData = [...data];

        sortableData.sort((a, b) => {
            // Handle nested properties (e.g., partType.name)
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'part_type.name') {
                aValue = a.part_type?.name;
                bValue = b.part_type?.name;
            } else if (sortConfig.key === 'product.sku') {
                aValue = a.product?.sku;
                bValue = b.product?.sku;
            } else if (sortConfig.key === 'status') {
                // For status sorting, compare based on low stock (true/false)
                const aLowStock = a.current_stock < a.threshold;
                const bLowStock = b.current_stock < b.threshold;

                // If sorting direction is ascending, low stock comes first
                // If direction is descending, sufficient stock comes first
                if (aLowStock !== bLowStock) {
                    return sortConfig.direction === 'asc'
                           ? (aLowStock ? -1 : 1)
                           : (aLowStock ? 1 : -1);
                }

                // If status is the same, sort by stock level as secondary criteria
                return sortConfig.direction === 'asc'
                       ? a.current_stock - b.current_stock
                       : b.current_stock - a.current_stock;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sortableData;
    };

    // Filter inventory data by search term
    const getFilteredData = (data) => {
        if (!searchTerm) return getSortedData(data);

        return getSortedData(data).filter(item => {
            const searchFields = [
                item.part_type?.name,
                item.product?.sku,
                item.current_stock?.toString(),
                item.threshold?.toString()
            ].filter(Boolean);

            return searchFields.some(field =>
                                         field.toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    };

    // Get general inventory data
    const getGeneralInventoryData = () => {
        return getFilteredData(inventory.general_inventory || []);
    };

    // Get SKU-specific inventory data
    const getSkuInventoryData = () => {
        return getFilteredData(inventory.sku_specific_inventory || []);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap sm:flex-nowrap">
                <h1 className="text-2xl font-semibold text-gray-800">Inventory Management</h1>

                <div className="flex space-x-2 my-3 sm:my-0">
                    {hasAnyRole(['admin', 'production']) && (
                        <>
                            <button
                                onClick={() => setIsStockInModalOpen(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                                <PlusIcon className="h-4 w-4 mr-1" /> Stock In
                            </button>

                            <button
                                onClick={() => setIsStockOutModalOpen(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <MinusIcon className="h-4 w-4 mr-1" /> Stock Out
                            </button>

                            <button
                                onClick={() => setIsAdjustModalOpen(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                            >
                                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-1" /> Adjust
                            </button>
                        </>
                    )}

                    <button
                        onClick={exportInventoryCSV}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" /> Export
                    </button>
                </div>
            </div>

            {/* Search and filters */}
            <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center space-x-4">
                    <div className="w-full max-w-xs">
                        <label htmlFor="search" className="sr-only">
                            Search
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg
                                    className="h-5 w-5 text-gray-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <input
                                id="search"
                                name="search"
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                placeholder="Search inventory"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Inventory Tabs */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                    <Tab.List className="flex bg-gray-50 p-1 space-x-1 border-b border-gray-200">
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                                    focus:outline-none focus:ring-0
                                    ${selected
                                      ? 'bg-white text-primary-700 shadow'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <TableCellsIcon className="w-5 h-5 mr-2" />
                                    SKU-Specific Inventory
                                  </span>
                                </button>
                            )}
                        </Tab>
                        <Tab as={Fragment}>
                            {({ selected }) => (
                                <button
                                    className={`
                    w-full py-2.5 text-sm leading-5 font-medium rounded-md
                    focus:outline-none focus:ring-0
                    ${selected
                      ? 'bg-white text-primary-700 shadow'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                  `}
                                >
                                  <span className="flex items-center justify-center">
                                    <TableCellsIcon className="w-5 h-5 mr-2" />
                                    General Inventory
                                  </span>
                                </button>
                            )}
                        </Tab>

                    </Tab.List>
                    <Tab.Panels>
                        {/* SKU-Specific Inventory Panel */}
                        <Tab.Panel>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('product.sku')}
                                        >
                                            <div className="flex items-center">
                                                SKU
                                                {sortConfig.key === 'product.sku' && (
                                                    <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('part_type.name')}
                                        >
                                            <div className="flex items-center">
                                                Part Type
                                                {sortConfig.key === 'part_type.name' && (
                                                    <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('current_stock')}
                                        >
                                            <div className="flex items-center">
                                                Current Stock
                                                {sortConfig.key === 'current_stock' && (
                                                    <span className="ml-1">
                                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('threshold')}
                                        >
                                            <div className="flex items-center">
                                                Threshold
                                                {sortConfig.key === 'threshold' && (
                                                    <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('on_order')}
                                        >
                                            <div className="flex items-center">
                                                On Order
                                                {sortConfig.key === 'on_order' && (
                                                    <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('last_received_at')}
                                        >
                                            <div className="flex items-center">
                                                Last Received
                                                {sortConfig.key === 'last_received_at' && (
                                                    <span className="ml-1">
                                                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('status')}
                                        >
                                            <div className="flex items-center">
                                                Status
                                                {sortConfig.key === 'status' && (
                                                    <span className="ml-1">
                                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                </span>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {getSkuInventoryData().length > 0 ? (
                                        getSkuInventoryData().map((item) => (
                                            <tr key={item.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {item.product?.sku}-{item.part_type?.part_identifier}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.part_type?.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.current_stock}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.threshold}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.on_order}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.last_received_at
                                                     ? new Date(item.last_received_at).toLocaleDateString()
                                                     : 'Never'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {item.current_stock < item.threshold ? (
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                            Low Stock
                                                          </span>
                                                    ) : (
                                                         <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                            Sufficient
                                                          </span>
                                                     )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                         <tr>
                                             <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                                 {searchTerm ? 'No matching SKU-specific inventory items found.' : 'No SKU-specific inventory items found.'}
                                             </td>
                                         </tr>
                                     )}
                                    </tbody>
                                </table>
                            </div>
                        </Tab.Panel>
                        {/* General Inventory Panel */}
                        <Tab.Panel>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('part_type.name')}
                                        >
                                            <div className="flex items-center">
                                                Part Type
                                                {sortConfig.key === 'part_type.name' && (
                                                    <span className="ml-1">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('current_stock')}
                                        >
                                            <div className="flex items-center">
                                                Current Stock
                                                {sortConfig.key === 'current_stock' && (
                                                    <span className="ml-1">
                                                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('threshold')}
                                        >
                                            <div className="flex items-center">
                                                Threshold
                                                {sortConfig.key === 'threshold' && (
                                                    <span className="ml-1">
                                                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('on_order')}
                                        >
                                            <div className="flex items-center">
                                                On Order
                                                {sortConfig.key === 'on_order' && (
                                                    <span className="ml-1">
                                                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('last_received_at')}
                                        >
                                            <div className="flex items-center">
                                                Last Received
                                                {sortConfig.key === 'last_received_at' && (
                                                    <span className="ml-1">
                                                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort('status')}
                                        >
                                            <div className="flex items-center">
                                                Status
                                                {sortConfig.key === 'status' && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {getGeneralInventoryData().length > 0 ? (
                                        getGeneralInventoryData().map((item) => (
                                            <tr key={item.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {item.part_type?.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.current_stock}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.threshold}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.on_order}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.last_received_at
                                                     ? new Date(item.last_received_at).toLocaleDateString()
                                                     : 'Never'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {item.current_stock < item.threshold ? (
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Low Stock
                              </span>
                                                    ) : (
                                                         <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Sufficient
                              </span>
                                                     )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                         <tr>
                                             <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                                 {searchTerm ? 'No matching inventory items found.' : 'No inventory items found.'}
                                             </td>
                                         </tr>
                                     )}
                                    </tbody>
                                </table>
                            </div>
                        </Tab.Panel>


                    </Tab.Panels>
                </Tab.Group>
            </div>

            {/* Modals */}
            <StockInModal
                isOpen={isStockInModalOpen}
                onClose={() => setIsStockInModalOpen(false)}
                onSuccess={handleOperationSuccess}
                partTypes={partTypes}
                products={products}
            />

            <StockOutModal
                isOpen={isStockOutModalOpen}
                onClose={() => setIsStockOutModalOpen(false)}
                onSuccess={handleOperationSuccess}
                partTypes={partTypes}
                products={products}
                inventory={inventory}
            />

            <AdjustInventoryModal
                isOpen={isAdjustModalOpen}
                onClose={() => setIsAdjustModalOpen(false)}
                onSuccess={handleOperationSuccess}
                partTypes={partTypes}
                products={products}
                inventory={inventory}
            />
        </div>
    );
};

export default Inventory;


